import { FC, useEffect, useState } from "react";
import { Textarea } from "rizzui";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import { toast } from "sonner";
import { useFormik } from "formik";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import * as yup from "yup";
import { ProviderTypeService } from "../../../services/providerTypes/providerTypeService";
import { ProviderType } from "../../../type/entities/provider-type-type";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";

interface ProviderTypeFormProps {
    setOpenModal: any;
    refetch: () => void;
    providerType?: ProviderType | null;
    formRef?: any;
}

const schema = yup.object({
    name: yup.string().required('El nombre es obligatorio'),
    description: yup.string().optional().nullable(),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const ProviderTypeForm:FC<ProviderTypeFormProps> = ({ setOpenModal, providerType, refetch, formRef }) => {

    // STATES

    const service = new ProviderTypeService();
    const isEdit = providerType !== null;
    const [isSearched, setIsSearched] = useState(false);
    
    // HOOKS
    
    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors }      = useHandleErrors();
    const { getCompaniesList }  = useCompanies();
    const { userCan }           = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: providerType?.id ?? '',
            name: providerType?.name ?? '',
            description: providerType?.description ?? '',
            company: providerType?.company?.id ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO TIPO DE PROVEEDOR
     * @EN CREATE A NEW PROVIDER TYPE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createProviderType(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Tipo de proveedor creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------
    

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN TIPO DE PROVEEDOR
     * @EN EDIT A PROVIDER TYPE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editProviderType(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Tipo de proveedor editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
         <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>
                        <RequiredInput
                            id='name'
                            label="Nombre"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.name}
                        />

                        <Textarea
                            id='description'
                            label='Descripción'
                            rows={4}
                            value={formik.values.description}
                            onChange={(e) => formik.setFieldValue('description', e.target.value)}
                            className="mb-4 md:col-span-12"
                        />

                        {userCan('list', 'company') && (
                            <CustomSelect
                                required
                                isSearchable
                                id={'company'}
                                label="Compañia"
                                containerClassName='md:col-span-12'
                                value={
                                    formik.values.company ?
                                    { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                    getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                                }
                                options={getCompaniesList()}
                                onChange={
                                    (e: any) => { formik.setFieldValue('company', e?.value) }
                                }
                                error={formik.errors.company}
                            />
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
}

export default ProviderTypeForm;
